import React, { useMemo } from 'react';
import {
  useReplyToState,
  useReplyToActions,
  ReplyToItem,
} from '../../../contexts/ReplyToContext';
import { ReactComponent as CloseIcon } from '../../../icons/close.svg';
import { ReactComponent as MessageQuestionIcon } from '../../../icons/message-question-circle.svg';
import './reply-to-preview.scss';

interface ColorVariants {
  base: string;
  midDarker: string;
  darker: string;
  lighter: string;
}

interface ReplyToPreviewProps {
  colorVariants?: ColorVariants | null;
}

/**
 * ReplyToPreview
 *
 * Displays a preview of what the user is replying to, shown above the textarea.
 * This component internally consumes the ReplyToStateContext, so only this
 * component re-renders when replyTo changes - not the parent ThreadView.
 *
 * Returns null if there's nothing to reply to.
 */
const ReplyToPreview: React.FC<ReplyToPreviewProps> = ({ colorVariants }) => {
  const { replyTo } = useReplyToState();
  const { removeReplyTo, clearReplyTo } = useReplyToActions();

  const nonBookable = useMemo(
    () => replyTo.filter((item) => !item.bookable),
    [replyTo],
  );
  const bookable = useMemo(
    () => replyTo.filter((item) => item.bookable),
    [replyTo],
  );

  const totalsByCurrency = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const item of bookable) {
      if (item.price != null && item.price_currency) {
        totals[item.price_currency] =
          (totals[item.price_currency] || 0) + item.price;
      }
    }
    return totals;
  }, [bookable]);

  if (!replyTo || replyTo.length === 0) {
    return null;
  }

  const formatPrice = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
      }).format(amount);
    } catch {
      return `${amount} ${currency}`;
    }
  };

  const renderItem = (item: ReplyToItem, index: number) => {
    if (item.glow || item.id === 'routine-creation') {
      return (
        <div key={item.id} className='reply-to-preview--glow'>
          <MessageQuestionIcon className='reply-to-preview--glow-icon' />
          <span className='reply-to-preview--glow-text'>{item.actionLine}</span>
        </div>
      );
    }

    return (
      <div
        key={item.id || index}
        className='reply-to-preview'
        style={
          colorVariants
            ? {
                background: colorVariants.lighter,
                borderLeftColor: colorVariants.darker,
              }
            : undefined
        }
      >
        <div className='reply-content'>
          <span
            className='action-line'
            style={colorVariants ? { color: colorVariants.darker } : undefined}
          >
            {item.actionLine}
          </span>
          <span className='description-line'>{item.descriptionLine}</span>
        </div>
        <button
          className='dismiss-button'
          onClick={() => removeReplyTo(item.id)}
          aria-label='Remove item'
        >
          <CloseIcon />
        </button>
      </div>
    );
  };

  const totalEntries = Object.entries(totalsByCurrency);
  const totalDisplay = totalEntries
    .map(([cur, amt]) => formatPrice(amt, cur))
    .join(' + ');

  const hasGlow = replyTo.some(
    (item) => item.glow || item.id === 'routine-creation',
  );

  return (
    <div
      className={`reply-to-preview-wrapper${hasGlow ? ' reply-to-preview-wrapper--glow-active' : ''}`}
    >
      {hasGlow && <div className='reply-to-glow' />}
      <div className='reply-to-preview-items'>
        {nonBookable.map(renderItem)}
        {bookable.length > 0 && (
          <div className='reply-to-bookable-group'>
            {bookable.map(renderItem)}
            <div className='reply-to-bookable-footer'>
              <small>You can ask me to book these</small>
              {totalDisplay && (
                <p>
                  <b>Total:</b> <span>{totalDisplay}</span>
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReplyToPreview;
